import { useEffect } from 'react';
import { Box } from '@chakra-ui/react';
import { Text } from '@kula-ai/components';
import { useFormikContext } from 'formik';
import { hasUsableValue } from '@/api/customFields/formulaWireUnits';
import { colors } from '@/theme/foundations/colors';
import { toControlValue } from './calculatedControlValue';
import type { ControlOption } from './calculatedControlValue';
import { describeDiagnostic } from '@/utils/formula/formulaDiagnostics';
import { useCalculatedValue } from './useCalculatedValue';
import type { CalculatedFieldState } from './calculatedField.interface';

export interface CalculatedFieldSyncProps {
  // The form field the result is written to, so the field's own control shows
  // it — a calculated Yes/No is the Yes/No control, not a box of text.
  name: string;
  formula: string;
  fieldNameOf: (token: string) => string;
  // The backend's verdict on the saved rule. Omitted — nothing sends it yet —
  // means show whatever the server computes now.
  state?: CalculatedFieldState;
  // The fields a 'blocked' rule is still waiting on, named for the recruiter.
  blockedBy?: string[];
  // Unlocked, the recruiter may type over the result, so it is theirs to keep.
  locked?: boolean;
  subjectType?: string;
  kind?: string;
  fieldId?: string | number;
  // A select's own options: the formula answers with their names, the control
  // holds their ids.
  options?: ControlOption[];
  multi?: boolean;
}

const blockedMessage = (fieldName: string) =>
  `Fill in the dependent "${fieldName}" field to see this value`;

// The one diagnostic a recruiter can clear themselves: the engine says this
// when it has no value to work with. Every other code is the formula's own
// fault and no amount of filling in will help.
const isMissingInput = (diagnostic?: { code?: string } | null) =>
  diagnostic?.code === 'err_formula_not_computable';

const Refusal = ({ children }: { children: string }) => (
  // kula Text drops data-* attributes, so the hook sits on the wrapper.
  <Box mt="1" data-test-id="calculated-blocked">
    <Text variant="small" style={{ color: colors['pastel-1-dark'] }}>
      {children}
    </Text>
  </Box>
);

// Sits under a calculated field: works the value out, puts it where the
// field's own control reads it, and says why when there is none to show.
// It renders no control of its own — the kind's config already does.
export const CalculatedFieldSync = ({
  name,
  formula,
  fieldNameOf,
  state,
  blockedBy,
  locked = true,
  subjectType,
  kind,
  fieldId,
  options,
  multi,
}: CalculatedFieldSyncProps) => {
  const { values, setFieldValue } = useFormikContext<Record<string, unknown>>();
  const {
    value: computed,
    missing,
    diagnostic,
  } = useCalculatedValue({
    formula,
    values,
    fieldNameOf,
    subjectType,
    kind,
    fieldId,
  });

  const waitingFor = blockedBy?.[0] ?? missing[0]?.label;
  const blocked = state === 'blocked' || (!state && Boolean(waitingFor));
  // The server has just worked this one out, from what the form holds now.
  const live = computed !== null && computed !== undefined;
  // A 'blocked' or 'error' rule can still carry a value from an earlier save,
  // so the state decides whether a SAVED value may be shown at all.
  //
  // It cannot decide that for a live one. `state` is the offer's verdict from
  // its last write, so it is stale the moment anyone types — a field saved
  // blocked stayed blank however the recruiter filled the field it was waiting
  // on, even while evaluate_formula was answering with the right number on
  // every keystroke (CORE-5539). `hidden_by_condition` had the same problem and
  // the same answer (ENG-4212): re-read live, and let the saved flag speak only
  // when there is nothing live to speak for it.
  const usable =
    live ||
    hasUsableValue({
      state: blocked ? 'blocked' : (state ?? 'ok'),
      value: computed,
    });

  const shown = usable
    ? toControlValue(kind, computed, { options, multi })
    : { value: '' };
  const next = shown.unstorable ? '' : shown.value;

  // Only a locked field publishes: an unlocked one would read its own result
  // back as an override and stop following its inputs, which is the opposite
  // of what a formula is for.
  useEffect(() => {
    if (!locked) return;
    if (JSON.stringify(values[name] ?? '') === JSON.stringify(next ?? ''))
      return;
    setFieldValue(name, next);
  }, [locked, name, next, setFieldValue, values]);

  if (shown.unstorable) return <Refusal>{shown.unstorable}</Refusal>;
  // "This formula cannot be worked out" is what the engine answers when an
  // input is empty, and on its own it is a dead end — the recruiter is the one
  // who can fill that box. Where we know which field is missing, name it
  // instead (CORE-5487).
  //
  // Said on an optional field too. The rule used to be that an optional field
  // simply stays blank, but a diagnostic was rendered regardless of mandatory,
  // so the quieter message was never what happened: the filler got the dead
  // end either way. Naming the field is strictly better than that.
  // A refusal the filler cannot clear comes first: it is the admin's to fix,
  // and telling the recruiter to fill a box would send them nowhere.
  if (diagnostic && !isMissingInput(diagnostic))
    return <Refusal>{describeDiagnostic(diagnostic)}</Refusal>;
  if (!live && waitingFor && (blocked || isMissingInput(diagnostic)))
    return <Refusal>{blockedMessage(waitingFor)}</Refusal>;
  // Nothing to work with and nothing to name: the generic line is all there is.
  if (diagnostic) return <Refusal>{describeDiagnostic(diagnostic)}</Refusal>;
  return null;
};
